/**
 * Хуки данных гостевого входа (Tanstack Query поверх @hsc/api-client).
 * Server-state только через React Query (патент проекта).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { keepPreviousData } from '@tanstack/react-query';
import {
  approveGuestAccess,
  cancelGuestAccess,
  createGuestAccess,
  listGuestAccess,
  listPendingGuestAccess,
  rejectGuestAccess,
  revokeGuestAccess,
} from '@hsc/api-client';
import type {
  ApproveGuestAccessInput,
  CreateGuestAccessInput,
  CreateGuestAccessResponse,
  GuestAccessGrant,
  GuestAccessListPageResponse,
  GuestAccessListQuery,
  GuestAccessListResponse,
} from '@hsc/types';

const KEY = ['guest-access'] as const;

/**
 * GET /v1/guest-access?status=&page=&page_size= — серверно-пагинированный
 * список грантов с фильтром-бакетом по статусу (экран «Список доступов»).
 * keepPreviousData — список не «мигает» при смене страницы/фильтра.
 */
export function useGuestAccessListPage(query: GuestAccessListQuery) {
  return useQuery<GuestAccessListPageResponse>({
    queryKey: [...KEY, 'list', query.status ?? '', query.page ?? 1, query.page_size ?? 20],
    queryFn: () => listGuestAccess(query),
    placeholderData: keepPreviousData,
  });
}

/** GET /v1/guest-access/pending — очередь на подтверждение. */
export function usePendingGuestAccess() {
  return useQuery<GuestAccessListResponse>({
    queryKey: [...KEY, 'pending'],
    queryFn: listPendingGuestAccess,
  });
}

/** POST /v1/guest-access — выдать доступ (персонал). */
export function useCreateGuestAccess() {
  const qc = useQueryClient();
  return useMutation<CreateGuestAccessResponse, Error, CreateGuestAccessInput>({
    mutationFn: createGuestAccess,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/** POST /v1/guest-access/{id}/approve — подтвердить (с коррекцией). */
export function useApproveGuestAccess() {
  const qc = useQueryClient();
  return useMutation<GuestAccessGrant, Error, { id: string; input?: ApproveGuestAccessInput }>({
    mutationFn: ({ id, input }) => approveGuestAccess(id, input ?? {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/** POST /v1/guest-access/{id}/reject — отклонить заявку. */
export function useRejectGuestAccess() {
  const qc = useQueryClient();
  return useMutation<GuestAccessGrant, Error, string>({
    mutationFn: (id) => rejectGuestAccess(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/** POST /v1/guest-access/{id}/revoke — отозвать выданный доступ. */
export function useRevokeGuestAccess() {
  const qc = useQueryClient();
  return useMutation<GuestAccessGrant, Error, string>({
    mutationFn: (id) => revokeGuestAccess(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/**
 * POST /v1/guest-access/{id}/revoke — отменить доступ из «Списка доступов».
 * Бэк переводит грант в `expired` («Просрочен») и рубит сессию гостя.
 */
export function useCancelGuestAccess() {
  const qc = useQueryClient();
  return useMutation<GuestAccessGrant, Error, string>({
    mutationFn: (id) => cancelGuestAccess(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
